import { useCallback, useState } from "react";

/**
 * Accent hue selector. The chromatic ramps live in `styles/tokens.css`
 * under `[data-accent="…"][data-theme="…"]` (mono = the neutral fg/bg
 * accent; the four hues share an OKLCH lightness so swapping preserves
 * WCAG contrast — see docs/design/v0.5/tokens.md). The bootstrap script
 * in index.html applies the initial `data-accent` before React mounts
 * (no flash); this hook owns the runtime switch + persistence.
 *
 * Default is `cobalt` (see `DEFAULT_ACCENT` below and the bootstrap in
 * index.html, which both agree) — a fresh install gets an on-brand hue
 * rather than a colourless slab.
 */
export const ACCENTS = ["mono", "cobalt", "solder", "pingora", "iris"] as const;
export type Accent = (typeof ACCENTS)[number];

const STORAGE_KEY = "aether:accent";
const DEFAULT_ACCENT: Accent = "cobalt";

function isAccent(v: unknown): v is Accent {
  return typeof v === "string" && (ACCENTS as readonly string[]).includes(v);
}

function readStored(): Accent {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isAccent(v)) return v;
  } catch {
    // localStorage may be unavailable (private mode, file://); fall through.
  }
  return DEFAULT_ACCENT;
}

function apply(accent: Accent) {
  document.documentElement.setAttribute("data-accent", accent);
}

export function useAccent(): { accent: Accent; setAccent(a: Accent): void } {
  const [accent, setAccentState] = useState<Accent>(readStored);

  const setAccent = useCallback((a: Accent) => {
    try {
      localStorage.setItem(STORAGE_KEY, a);
    } catch {
      // ignore — attribute still applies for the session.
    }
    apply(a);
    setAccentState(a);
  }, []);

  return { accent, setAccent };
}
